import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { WeightedReturnType } from '../../../features/performance/performance.domain';
import { AccountsDescriptionService } from '../../../features/accounts/services/accounts-description.service';
import { GoalStatusTypeHelper, LtiAccountStatus } from '../../../domain/lti/lti.enum';
import { RestService } from '../../../services/rest/rest.service';
import { RateOfReturn } from '../../../domain/balances.domain';
import { RestOptions } from '../../../services/rest/rest-request';
import { SessionStorageService } from '../../../services/storage/session-storage.service';
import { InvestmentGoal, LtiAccount, LtiPortfolio, RetirementGoal } from '../../../domain/lti/lti.domain';

@Injectable({
  providedIn: 'root'
})
export class DashboardRestService {
  private readonly restService = inject(RestService);
  private readonly accountsDescriptionService = inject(AccountsDescriptionService);
  private readonly sessionStorageService = inject(SessionStorageService);

  private static readonly COMPLETED_GOALS_LIST: string = 'completedGoalsList';

  retrieveGoals(onlyCompleted: boolean = false): Observable<Array<InvestmentGoal>> {
    return this.restService.getResource('wealthInvestment.getGoals').get<Array<InvestmentGoal>>()
      .pipe(map((goals: Array<InvestmentGoal>): Array<InvestmentGoal> => {
        if (onlyCompleted) {
          return goals.filter((goal: InvestmentGoal): boolean => GoalStatusTypeHelper.isGoalSetupComplete(goal.status));
        }
        this.copyGoalsIntoSessionStorage(goals);
        return goals;
      }));
  }

  getGoalsWithObjective(params: Record<string, unknown>): Observable<Array<InvestmentGoal>> {
    return this.restService.getResource('wealthInvestment.getGoalsWithObjective').get<Array<InvestmentGoal>>({ params });
  }

  getGoalDetails(goalId: string): Observable<InvestmentGoal> {
    return this.getGoalsWithObjectiveAndModel({ goalId })
      .pipe(map((goals: Array<InvestmentGoal>): InvestmentGoal | undefined => {
        const hasGoals = goals !== undefined && goals.length > 0;
        return hasGoals ? goals[0] : undefined;
      }));
  }

  getGoalsWithObjectiveAndModel(params: Record<string, unknown>): Observable<Array<InvestmentGoal>> {
    return this.restService.getResource('wealthInvestment.getGoalsWithObjectiveAndModel').get<Array<InvestmentGoal>>({ params });
  }

  deleteInvestmentGoal(pathVariables: Record<string, string>): Observable<void> {
    return this.restService.getResource('wealthInvestment.deleteInvestmentGoal').del<void>(undefined,
      { pathVariables });
  }

  updateInvestmentGoal(pathVariables: Record<string, string>, investmentGoal: RetirementGoal): Observable<InvestmentGoal> {
    return this.restService.getResource('wealthInvestment.updateInvestmentGoal').patch<InvestmentGoal>(investmentGoal,
      { pathVariables });
  }

  getAccounts(params: Record<string, string>): Observable<Array<LtiAccount>> {
    return this.restService.getResource('wealthInvestment.getAccountsForGoals').get<Array<LtiAccount>>({ params }).pipe(
      map((accounts: Array<LtiAccount>): Array<LtiAccount> => this.accountsDescriptionService.setDescriptions(accounts))
    );
  }

  hasLtiEligibleAccounts(): Observable<boolean> {
    const params: RestOptions = {
      pathVariables: {
        subtype: 'LTI'
      }, params: {
        status: LtiAccountStatus.LTI_ELIGIBLE
      }
    };
    return this.restService.getResource('wealthInvestment.getInvestmentAccounts').get<Array<LtiAccount>>(params)
      .pipe(map((accounts: Array<LtiAccount>): boolean => accounts.length > 0));
  }

  cloneGoal(pathVariables: Record<string, unknown>, investmentGoal?: RetirementGoal): Observable<InvestmentGoal> {
    return this.restService.getResource('wealthInvestment.cloneGoal').post(investmentGoal,
      { pathVariables });
  }

  copyGoal(pathVariables: Record<string, unknown>, investmentGoal?: InvestmentGoal, deleteSourceGoal?: boolean): Observable<unknown> {
    return this.restService.getResource('wealthInvestment.copyGoal').patch({ investmentGoal, deleteSourceGoal },
      { pathVariables });
  }

  getRateOfReturn(goalId: string, weightedReturnType?: WeightedReturnType): Observable<RateOfReturn> {
    const params: RestOptions = {
      pathVariables: {
        goalId
      }, params: {
        weightedReturnType
      }
    };
    return this.restService.getResource('wealthInvestment.rateOfReturn').get<RateOfReturn>(params);
  }

  getHoldings(goalId: string): Observable<LtiPortfolio> {
    const params: RestOptions = {
      pathVariables: {
        goalId
      }
    };
    return this.restService.getResource('wealthInvestment.holdings').get<LtiPortfolio>(params);
  }

  private copyGoalsIntoSessionStorage(goals: Array<InvestmentGoal>): void {
    this.sessionStorageService.removeItem(DashboardRestService.COMPLETED_GOALS_LIST);
    const completedGoals = goals.filter((goal: InvestmentGoal): boolean => GoalStatusTypeHelper.isGoalSetupComplete(goal.status));
    const goalIDsToBeStored: Array<string> = completedGoals.map((goal: InvestmentGoal)=> goal.id);
    this.sessionStorageService.setItem(DashboardRestService.COMPLETED_GOALS_LIST, goalIDsToBeStored);
  }
}

